import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Pagination } from 'antd';
import { toQuery, useGetCharacterizationReportsQuery } from 'services/exploreService';
import { Empty, PageHead, Panel, Skeleton, fmt, studyHref } from 'components/explore';
import { characterizationLabel } from '.';
import { useUrlFilters } from 'hooks/useUrlFilters';
import CohortFilters from './CohortFilters';

const PAGE_SIZE = 25;
const CharacterizationDetail: React.FC = () => {
  const { key = '' } = useParams<{ key: string }>();
  const { filters, page, update, clear, activeCount } = useUrlFilters(PAGE_SIZE);
  const cohort = { ...filters };
  delete cohort.offset;
  delete cohort.limit;
  delete cohort.sort;
  delete cohort.order;
  const studiesHref = `/studies${toQuery({ ...cohort, characterization: key })}`;
  const {
    currentData: data,
    isFetching,
    error,
    refetch,
  } = useGetCharacterizationReportsQuery({
    key,
    ...filters,
  });
  return (
    <div className="ax-page">
      <PageHead
        eyebrow={<Link to="/characterizations">← Characterization Search</Link>}
        title={data ? characterizationLabel(data.characterization) : 'Characterization'}
        lede={
          data
            ? `${fmt(data.total)} linked reports across ${fmt(data.characterization.studies)} studies${activeCount ? ' matching the filters' : ''}. These are the reports coded with this exact characterization, not free-text matches or a study’s latest report.`
            : undefined
        }
        actions={
          data ? (
            <Link className="ax-link" to={studiesHref}>
              Browse linked studies →
            </Link>
          ) : undefined
        }
      />
      <CohortFilters filters={filters} update={update} clear={clear} activeCount={activeCount} />
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Could not load characterization reports"
          description="The characterization may no longer exist, or its reports could not be loaded."
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      ) : !data || isFetching ? (
        <Skeleton h={200} />
      ) : data.items.length === 0 ? (
        <Panel>
          <Empty title="No linked reports">
            This characterization has no reports on this page.
          </Empty>
        </Panel>
      ) : (
        data.items.map(report => (
          <Panel
            key={report.uuid}
            eyebrow={`Report ${report.icid || report.uuid}`}
            title={
              <Link className="ax-link" to={studyHref(report.study_icid || report.study_uuid)}>
                {report.study_icid || report.study_uuid}
              </Link>
            }
            sub={[
              report.datasource,
              report.visit_year,
              report.age_at_visit && `Age at visit: ${report.age_at_visit}`,
              report.signer && `Signed by ${report.signer}`,
              report.date_created && new Date(report.date_created).toLocaleDateString(),
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <div style={{ display: 'grid', gap: 8 }}>
              {report.matching_findings.map((finding, i) => (
                <p key={i} className="ax-note" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {finding}
                </p>
              ))}
              <details>
                <summary className="ax-link" style={{ cursor: 'pointer' }}>
                  Report text
                </summary>
                {report.sections.length ? (
                  report.sections.map(section => (
                    <div key={section.key} style={{ marginTop: 12 }}>
                      <b>{section.key.replace(/_/g, ' ')}</b>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{section.text}</p>
                    </div>
                  ))
                ) : (
                  <p className="ax-faint">No report text stored.</p>
                )}
              </details>
            </div>
          </Panel>
        ))
      )}
      {data && (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={data.total}
          showSizeChanger={false}
          disabled={isFetching}
          showTotal={total => `${fmt(total)} reports`}
          onChange={next => update({ page: next })}
        />
      )}
    </div>
  );
};
export default CharacterizationDetail;
